import {
  Link,
  Outlet,
  createRootRoute,
  redirect,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { ChevronDown, LogOut, Plus } from "lucide-react";
import { useState } from "react";

import { GetStartedDrawer } from "@/components/GetStartedDrawer.tsx";
import { ModeToggle } from "@/components/ModeToggle.tsx";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { ApiError, UnauthorizedError, apiGet } from "@/lib/api.ts";
import { clearActiveOrgId, getActiveOrgId, setActiveOrgId } from "@/lib/activeOrg.ts";
import { type OnboardingState, dismissOnboardingDot, fetchOnboarding } from "@/lib/onboarding.ts";

type MeOrg = { id: string; slug: string; name: string; role: string };
type Me = {
  userId: string;
  organizationId: string;
  user: { id: string; name: string | null; email: string } | null;
  organizations: MeOrg[];
};

// Routes that don't need an org context. `/login` runs before sign-in;
// `/orgs/new` runs when the user has zero orgs so we can't require one. The
// root loader skips the /me fetch on these to avoid 401/400 noise.
const PUBLIC_ROUTES = new Set(["/login"]);
const ZERO_ORG_ROUTES = new Set(["/orgs/new"]);

export const Route = createRootRoute({
  loader: async ({ location }) => {
    if (PUBLIC_ROUTES.has(location.pathname)) return { me: null, onboarding: null };
    try {
      // /me is the auth gate; onboarding is decorative. Fetch in parallel —
      // onboarding errors are swallowed inside `fetchOnboarding`, so its
      // failure can't take down the loader.
      const [me, onboarding] = await Promise.all([apiGet<Me>("/api/v1/me"), fetchOnboarding()]);
      return { me, onboarding };
    } catch (err) {
      // Leaf routes own the redirect-to-login logic; for them we return
      // `me: null` and let them bounce. For `/orgs/new` (which doesn't
      // redirect) we still return null — the create flow doesn't need /me.
      if (err instanceof UnauthorizedError) return { me: null, onboarding: null };
      // FRE-289: 400 no_active_org → user has no memberships. Bounce to
      // /orgs/new. The create page is already in ZERO_ORG_ROUTES, so we
      // don't loop.
      if (ZERO_ORG_ROUTES.has(location.pathname)) return { me: null, onboarding: null };
      if (err instanceof ApiError && err.status === 400 && err.message.includes("no_active_org")) {
        throw redirect({ to: "/orgs/new" });
      }
      throw err;
    }
  },
  component: RootComponent,
});

function RootComponent() {
  const { me, onboarding } = Route.useLoaderData();
  const router = useRouter();
  const pathname = router.state.location.pathname;
  const hideHeader = PUBLIC_ROUTES.has(pathname) || ZERO_ORG_ROUTES.has(pathname);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {hideHeader ? null : <Header me={me} onboarding={onboarding} />}
      <main className={hideHeader ? "" : "px-6 py-8"}>
        <Outlet />
      </main>
    </div>
  );
}

function Header({ me, onboarding }: { me: Me | null; onboarding: OnboardingState | null }) {
  const hasOrgs = me && me.organizations.length > 0;
  return (
    <header className="flex items-center justify-between gap-4 border-b border-border px-6 py-3">
      <Link to="/" className="font-semibold text-foreground">
        Drop
      </Link>
      <div className="flex items-center gap-3">
        {me ? <GetStartedLink onboarding={onboarding} me={me} /> : null}
        {me ? <OrgSwitcher me={me} /> : null}
        {hasOrgs ? (
          <Link
            to="/org/members"
            className="text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            Members
          </Link>
        ) : null}
        {hasOrgs ? (
          <Link
            to="/settings/tokens"
            className="text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
          >
            Settings
          </Link>
        ) : null}
        <ModeToggle />
        {me ? <UserMenu user={me.user} /> : null}
      </div>
    </header>
  );
}

// FRE-290: "Get started" header link + unseen dot + drawer trigger.
//
// The dot is visible when `dotDismissedAt IS NULL`. Click opens the drawer
// (vaul via shadcn Drawer; non-modal so the page behind stays interactive)
// and fires PATCH /api/v1/me/onboarding to record the dismissal. We update
// local state optimistically — the dot disappears immediately, the network
// call settles in the background. A failed PATCH is silently retried on the
// next drawer open since `dotDismissedAt` is still null on the server.
function GetStartedLink({ onboarding, me }: { onboarding: OnboardingState | null; me: Me }) {
  const [dismissed, setDismissed] = useState<boolean>(() => onboarding?.dotDismissedAt != null);
  const [open, setOpen] = useState(false);

  function handleOpen() {
    setOpen(true);
    if (!dismissed) {
      setDismissed(true);
      void dismissOnboardingDot().catch(() => {
        // Keep the optimistic flip — re-fetching on next loader run will
        // either confirm or re-show the dot if the PATCH genuinely failed.
      });
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="relative inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:underline"
      >
        Get started
        {dismissed ? null : (
          <span aria-label="New" className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
        )}
      </button>
      <GetStartedDrawer
        open={open}
        onOpenChange={setOpen}
        onboarding={onboarding}
        organizations={me.organizations}
        activeOrganizationId={me.organizationId}
      />
    </>
  );
}

function OrgSwitcher({ me }: { me: Me }) {
  const navigate = useNavigate();
  const router = useRouter();
  const orgs = me.organizations;

  // Resolve the currently-active org: prefer the localStorage hint (what the
  // worker actually sees on every request via `X-Active-Org`), then fall back
  // to /me's reported active org, then first in list.
  const hintId = getActiveOrgId();
  const activeFromHint = hintId ? (orgs.find((o) => o.id === hintId) ?? null) : null;
  const activeFromMe = orgs.find((o) => o.id === me.organizationId) ?? null;
  const active = activeFromHint ?? activeFromMe ?? orgs[0] ?? null;

  if (orgs.length === 1) {
    // Single-org users get plain text — no point in a dropdown with one item.
    return (
      <span className="text-sm font-medium text-foreground" title={orgs[0].slug}>
        {orgs[0].name}
      </span>
    );
  }

  async function handleSelect(id: string) {
    if (id === active?.id) return;
    await setActiveOrgId(id);
    // invalidate() refetches the root loader so /me + the file list reflect
    // the new org. We also navigate home so the typical "switch + see my
    // files" expectation holds.
    await router.invalidate();
    await navigate({ to: "/" });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="outline" size="sm">
            <span className="max-w-[160px] truncate">{active?.name ?? "Select org"}</span>
            <ChevronDown className="size-3.5 opacity-70" data-icon="inline-end" />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="min-w-[220px]">
        {orgs.map((org) => {
          const isActive = org.id === active?.id;
          return (
            <DropdownMenuItem
              key={org.id}
              onClick={() => void handleSelect(org.id)}
              className={isActive ? "font-semibold" : undefined}
            >
              <div className="flex min-w-0 flex-col">
                <span className="truncate">{org.name}</span>
                <span className="truncate font-mono text-xs text-muted-foreground">{org.slug}</span>
              </div>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        <DropdownMenuItem render={<Link to="/orgs/new" />}>
          <Plus className="size-4" />
          <span>Create new org</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu({ user }: { user: Me["user"] }) {
  const router = useRouter();
  const navigate = useNavigate();

  async function handleSignOut() {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        origin: window.location.origin,
      },
      body: "{}",
    });
    // Clear the local active-org hint so the next user signing in on this
    // browser doesn't inherit it (and immediately hit a 403).
    clearActiveOrgId();
    await router.invalidate();
    await navigate({ to: "/login", search: { next: "/" } });
  }

  const initials =
    user?.name?.charAt(0).toUpperCase() ?? user?.email.charAt(0).toUpperCase() ?? "?";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="outline" size="icon" aria-label="Account menu">
            <span className="text-xs font-semibold">{initials}</span>
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="min-w-[220px]">
        {user ? (
          <div className="px-3 py-2 text-sm">
            <div className="font-medium text-foreground">{user.name ?? user.email}</div>
            {user.name ? (
              <div className="truncate text-xs text-muted-foreground">{user.email}</div>
            ) : null}
          </div>
        ) : null}
        {user ? <Separator className="my-1" /> : null}
        <DropdownMenuItem onClick={() => void handleSignOut()} variant="destructive">
          <LogOut className="size-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
